import { call, put, select, takeLatest } from 'redux-saga/effects'
import { INITIALIZE, SET_ROWS, SUBMIT, UPDATE_ROWS_STATE } from 'modules/Services/CampaignsEditor/subcomponents/BudgetEditor/types'
import { getCurrentResourceState } from 'modules/Services/CampaignsEditor/saga'
import NETWORKS_VK_ADS_V1 from 'api/networks/vk-ads/v1'
import omit from 'lodash/omit'
import { UPDATE_ITEM_BY_ID } from 'modules/Services/CampaignsEditor/types'
import pick from 'lodash/pick'
import extractErrors from 'utils/extractErrors'
import { notificationShow } from 'modules/Notifications/actions'

function* initialize() {
  const { items: currentItems, selection } = yield call(getCurrentResourceState)
  const ids = Object.keys(selection.ids).map((id) => +id)

  const rows = currentItems.filter(
    (row) => ids.includes(row.external_id) && row.status !== 'deleted',
  )

  yield put({ type: SET_ROWS, payload: { rows } })
}

function* submitResource({ resource, data }) {
  let METHOD = null

  switch (resource) {
    case 'campaigns':
      METHOD = NETWORKS_VK_ADS_V1.updateCampaigns
      break
    case 'ad-groups':
      METHOD = NETWORKS_VK_ADS_V1.updateAdGroups
      break
  }

  return yield call(METHOD, {
    data,
  })
}

function* submitType({ type, resource, data }) {
  if (data.length === 0) {
    return null
  }

  try {
    const { response, error } = yield call(submitResource, {
      resource,
      data,
    })

    if (error) {
      yield put({
        type: UPDATE_ROWS_STATE,
        payload: {
          type,
          items: data.reduce((acc, r) => {
            acc[r.external_id] = {
              rowState: {
                touched: true,
                error: error,
              },
            }
            return acc
          }, {}),
        },
      })

      throw error
    }

    const { success, failed } = response.data.reduce(
      (acc, row) => {
        if (row.status === 'success') {
          acc.success = [...acc.success, row]
        } else {
          acc.failed = [...acc.failed, row]
        }
        return acc
      },
      { success: [], failed: [] },
    )

    if (success.length > 0) {
      yield put({
        type: UPDATE_ROWS_STATE,
        payload: {
          type,
          items: success.reduce((acc, r) => {
            acc[r.external_id] = {
              rowState: { touched: false, updated: true, error: null },
            }
            return acc
          }, {}),
        },
      })

      yield put({
        type: UPDATE_ITEM_BY_ID,
        payload: success.map((successRow) =>
          data.find((item) => item.external_id === successRow.external_id),
        ),
      })
    }

    if (failed.length > 0) {
      yield put({
        type: UPDATE_ROWS_STATE,
        payload: {
          type,
          items: failed.reduce((acc, r) => {
            acc[r.external_id] = {
              rowState: {
                touched: true,
                error: r.error,
                errors: extractErrors(r.errors).fields,
              },
            }
            return acc
          }, {}),
        },
      })

      return { error: 'has unsubmitted rows' }
    }

    return { response: 'successfully' }
  } catch (error) {
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_update_failed',
      }),
    )

    return { error }
  }
}

function* submit({
  payload: { resource },
  meta: { resolve = () => {}, reject = () => {} } = {},
}) {
  const { budgetRows, datesRows } = yield select((state) => ({
    budgetRows: state['services/budget-editor'].budget.rows
      .filter((r) => r?.rowState?.touched === true)
      .map((row) => omit(row, 'rowState')),
    datesRows: state['services/budget-editor'].dates.rows
      .filter((r) => r?.rowState?.touched === true)
      .map((row) => omit(row, 'rowState')),
  }))

  try {
    const budgetResp = yield call(submitType, {
      type: 'budget',
      resource,
      data: budgetRows.map((r) =>
        pick(r, 'external_id', 'budget_limit', 'budget_limit_day'),
      ),
    })

    const datesResp = yield call(submitType, {
      type: 'dates',
      resource,
      data: datesRows.map((r) =>
        pick(r, 'external_id', 'date_start', 'date_end'),
      ),
    })

    if (budgetResp?.error || datesResp?.error) {
      throw new Error('submitting error')
    }

    resolve()
  } catch (e) {
    console.logXHR(e)
    reject('budget editor submit error')
  }
}

export default function* Sagas() {
  yield takeLatest(INITIALIZE, initialize)
  yield takeLatest(SUBMIT, submit)
}
